/**
 * The meetings list.
 *
 * **A call is two people already talking; a meeting has a link, a time,
 * invitees who may have no account, and a lobby.** They are two concepts and
 * relay-server keeps them in two files. The browser follows: a call belongs to
 * a conversation and is started from its header, and this surface is only ever
 * about meetings.
 *
 * The room itself is `pages/MeetingRoom.tsx` over
 * `components/meeting/MeetingRoom`, which is the same room the guest door
 * opens. This file had a second, thinner one until 2026-09-17; two rooms is two
 * lobbies and one of them would have been the one nobody fixed.
 */

import React, { useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { useT } from '../i18n';
import { formatDay, formatTime } from '../i18n/format';
import { store, useStore } from '../lib/relay/store';
import { Empty, PanelHeader } from '../components/Shell';
import { onChromeIntent, takeChromeIntent } from '../state/chrome';
import type { Meeting, MeetingInvitee, MeetingStatus, Person } from '../lib/relay/types';
import '../components/chat/chat.css';

const TONE: Record<MeetingStatus, 'signal' | 'neutral' | 'primary'> = {
  live: 'signal',
  scheduled: 'primary',
  ended: 'neutral',
  cancelled: 'neutral'
};

/**
 * A meeting in the list, and what its host can do to it from here.
 *
 * Join is everybody's — a meeting is a link, and holding the link is the whole
 * of what joining takes. The other four are the host's: copy the link, rotate
 * it (which is what revokes the old one), end it while it is live, cancel it
 * while it is not.
 */
function MeetingRow({ meeting, active }: { meeting: Meeting; active: boolean }) {
  const { Badge, Button } = ds();
  const t = useT();
  const [note, setNote] = useState('');

  const copy = async (rotate: boolean) => {
    const url = await store.joinLink(meeting.id, rotate ? { rotate: true } : undefined);
    await navigator.clipboard?.writeText(url);
    setNote(rotate ? t('meetings.wasreset') : t('meetings.copied'));
  };

  const over = meeting.status === 'ended' || meeting.status === 'cancelled';

  return (
    <div
      className="mv-row rl-meetingrow"
      style={{ background: active ? 'var(--app-panel-alt)' : undefined }}
    >
      <div className="rl-meetingrow-line">
        <span style={{ flex: '1 1 240px', minWidth: 0 }}>
          <span className="rl-truncate" style={{ display: 'block', fontWeight: 600 }}>
            {meeting.title}
          </span>
          {meeting.scheduledAt > 0 && (
            <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 13 }}>
              {formatDay(meeting.scheduledAt)} · {formatTime(meeting.scheduledAt)}
            </span>
          )}
        </span>
        <Badge tone={TONE[meeting.status]} dot={meeting.status === 'live'}>
          {t(`meetings.status.${meeting.status}`)}
        </Badge>

        {!over && (
          <div className="rl-meetingrow-actions">
            <Link to={`/meetings/${meeting.id}`}>
              <Button size="sm">{t('meetings.join')}</Button>
            </Link>
            {meeting.own && (
              <>
                <Button size="sm" variant="secondary" onClick={() => void copy(false)}>
                  {t('meetings.copy')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void copy(true)}>
                  {t('meetings.reset')}
                </Button>
                {meeting.status === 'live' ? (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => void store.endMeeting(meeting.id)}
                  >
                    {t('meetings.end')}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void store.cancelMeeting(meeting.id)}
                  >
                    {t('meetings.cancel')}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* A meeting that is over has one thing left: what was said in it. */}
      {meeting.status === 'ended' && (
        <div style={{ marginTop: 'var(--space-8)' }}>
          <Link to={`/meetings/${meeting.id}/transcript`}>
            <Button size="sm" variant="secondary">
              {t('call.transcript.title')}
            </Button>
          </Link>
        </div>
      )}

      {note && (
        <div style={{ marginTop: 'var(--space-6)', color: 'var(--text-muted)', fontSize: 13 }}>
          {note}
        </div>
      )}
    </div>
  );
}

/**
 * A new meeting.
 *
 * Meet now or at a time; invitees by username or email address; optionally
 * inside a conversation, in which case the server takes that conversation's
 * members as the invitees and merges whoever was named here.
 *
 * **Event style is the policy, not a mode.** It sets
 * `{mic:'host', camera:'host', screen:'host', lobby:'everyone'}` — the shape
 * the contract names — so a cohost is how somebody else gets to speak.
 */
function NewMeetingDialog({ onClose }: { onClose: () => void }) {
  const { Button, Dialog, Input, Radio, Select, Switch, Tag } = ds();
  const t = useT();
  const conversations = useStore((s) => s.conversations());

  const [title, setTitle] = useState('');
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [at, setAt] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [invitees, setInvitees] = useState<Person[]>([]);
  const [who, setWho] = useState('');
  const [missing, setMissing] = useState(false);
  const [event, setEvent] = useState(false);

  const add = async () => {
    const needle = who.trim();
    if (!needle) return;
    const person = await store.lookup(needle);
    if (!person) {
      setMissing(true);
      return;
    }
    setInvitees((list) => (list.some((p) => p.id === person.id) ? list : [...list, person]));
    setWho('');
  };

  const create = async () => {
    const name = title.trim();
    if (!name) return;
    const scheduledAt = when === 'later' && at ? new Date(at).getTime() : 0;
    const roster: MeetingInvitee[] = invitees.map((p) => ({ userId: p.id, role: 'attendee' }));

    await store.createMeeting({
      title: name,
      ...(scheduledAt ? { scheduledAt } : {}),
      ...(conversationId ? { conversationId } : {}),
      ...(roster.length ? { invitees: roster } : {}),
      ...(event
        ? { policy: { mic: 'host', camera: 'host', screen: 'host', lobby: 'everyone' } as const }
        : {})
    });
    onClose();
  };

  return (
    <Dialog
      open
      position="fixed"
      title={t('meetings.new')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void create()}>{t('meetings.new.create')}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--space-16)' }}>
        <Input
          label={t('meetings.new.name')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <Radio
          value={when}
          direction="row"
          options={[
            { value: 'now', label: t('meetings.new.now') },
            { value: 'later', label: t('meetings.new.later') }
          ]}
          onChange={(value: string) => setWhen(value === 'later' ? 'later' : 'now')}
        />

        {when === 'later' && (
          <Input
            label={t('meetings.new.time')}
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
          />
        )}

        <div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-8)' }}>
            <Input
              label={t('meetings.new.find')}
              value={who}
              autoComplete="off"
              error={missing ? t('meetings.new.notfound') : undefined}
              style={{ flex: 1 }}
              onChange={(e) => {
                setWho(e.target.value);
                setMissing(false);
              }}
            />
            <Button variant="secondary" onClick={() => void add()}>
              {t('common.add')}
            </Button>
          </div>
          {invitees.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', marginTop: 'var(--space-8)' }}>
              {invitees.map((p) => (
                <Tag
                  key={p.id}
                  onRemove={() => setInvitees((list) => list.filter((x) => x.id !== p.id))}
                >
                  {p.name}
                </Tag>
              ))}
            </div>
          )}
        </div>

        <Select
          label={t('meetings.new.conversation')}
          value={conversationId}
          options={[
            { value: '', label: t('meetings.new.conversation.none') },
            ...conversations
              .filter((c) => c.kind !== 'self' && !c.official)
              .map((c) => ({ value: c.id, label: c.title }))
          ]}
          onChange={(e) => setConversationId(e.target.value)}
        />

        <Switch
          label={t('meetings.new.event')}
          description={t('meetings.new.event.hint')}
          checked={event}
          onChange={(next: boolean) => setEvent(next)}
        />
      </div>
    </Dialog>
  );
}

const GROUPS: { key: string; status: MeetingStatus[] }[] = [
  { key: 'meetings.live', status: ['live'] },
  { key: 'meetings.scheduled', status: ['scheduled'] },
  { key: 'meetings.ended', status: ['ended', 'cancelled'] }
];

export function MeetingsList() {
  const { Button, IconButton } = ds();
  const t = useT();
  const { path } = useRouter();
  const meetings = useStore((s) => s.meetings());
  const [making, setMaking] = useState(false);

  // The title bar's Meet now. See the same block in `Chats.tsx`.
  useEffect(() => {
    if (takeChromeIntent('new-meeting')) setMaking(true);
    return onChromeIntent((intent) => {
      if (intent === 'new-meeting') setMaking(true);
    });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        title={t('meetings.title')}
        actions={<IconButton icon="Plus" label={t('meetings.new')} onClick={() => setMaking(true)} />}
      />
      {meetings.length === 0 ? (
        <Empty
          title={t('meetings.empty.title')}
          body={t('meetings.empty.body')}
          action={<Button onClick={() => setMaking(true)}>{t('meetings.new')}</Button>}
        />
      ) : (
        <div className="mv-scroll" style={{ flex: 1, padding: 'var(--space-8) 0' }}>
          {GROUPS.map((group) => {
            const rows = meetings.filter((m) => group.status.includes(m.status));
            if (rows.length === 0) return null;
            return (
              <div key={group.key}>
                <SectionLabel>{t(group.key)}</SectionLabel>
                {rows.map((m) => (
                  <MeetingRow key={m.id} meeting={m} active={path === `/meetings/${m.id}`} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {making && <NewMeetingDialog onClose={() => setMaking(false)} />}
    </div>
  );
}

/** The house eyebrow: mono, uppercase, tracked, faint. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--space-12) var(--space-16) var(--space-4)',
        fontFamily: 'var(--font-mono), monospace',
        fontSize: 11,
        letterSpacing: 'var(--ls-label)',
        textTransform: 'uppercase',
        color: 'var(--text-faint)'
      }}
    >
      {children}
    </div>
  );
}
